/**
 * Staff onboarding wizard — back-office page.
 *
 * Reached at `/staff/onboarding?token=…`. The provisioning token is minted
 * in the main client (Settings → Account) which then forwards the user to
 * this back-office origin. This page is where a staff identity is actually
 * created — it lives ONLY here, never in the public client.
 *
 * Resumability (the whole point of the pre-token system):
 *   - First load swaps the single-use provisioning token for a long-lived
 *     setup-session token, then wraps it in a `staff_onboarding` pre-token
 *     and rewrites the URL to `?token=<pre-token>`.
 *   - Any reload / "come back later" resolves that pre-token, restores the
 *     setup session, reads the authoritative status from `staff_accounts`
 *     and drops the user back on the exact step they left:
 *
 *         pending_profile → pending_security → completed
 *
 * The setup session token is kept server-side (inside the pre-token's
 * Redis payload) and mirrored in sessionStorage purely as a resilience
 * fallback — it is never placed in the URL.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "../api/staff";
import {
  staffSetupInit,
  staffSetupSaveProfile,
  staffSetupSecurityChallenge,
  staffSetupSecurityVerify,
  staffSetupState,
  type StaffSetupStatus,
  type StaffSetupUser,
} from "../api/staff";
import {
  resumePreToken,
  revokePreToken,
  savePreTokenStep,
  startPreToken,
} from "../api/preToken";
import { CheckIcon, ShieldIcon } from "../components/icons";
import { deriveKeyPair } from "../crypto/keys";
import * as mnemonic from "../crypto/mnemonic";
import { hex, sign } from "../crypto/sign";

const SS_KEY = "staff_onboarding_setup_token";
const SS_PRETOKEN = "staff_onboarding_pre_token";
const STAFF_REGISTER_MESSAGE_PREFIX = "dr:staff-register:v1";

type Phase = "loading" | "error" | "profile" | "security" | "completed";

const STEPS: ReadonlyArray<{ id: StaffSetupStatus; label: string }> = [
  { id: "pending_profile", label: "Profile" },
  { id: "pending_security", label: "Security" },
  { id: "completed", label: "Active" },
];

function statusToPhase(status: StaffSetupStatus): Phase {
  if (status === "pending_profile") return "profile";
  if (status === "pending_security") return "security";
  return "completed";
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Pick `count` distinct random indices in [0, length). */
function pickIndices(length: number, count: number): number[] {
  const all = Array.from({ length }, (_, i) => i);
  const out: number[] = [];
  while (out.length < count && all.length > 0) {
    const i = Math.floor(Math.random() * all.length);
    out.push(all.splice(i, 1)[0]!);
  }
  return out.sort((a, b) => a - b);
}

function Stepper({ status }: { status: StaffSetupStatus }) {
  const current = STEPS.findIndex((s) => s.id === status);
  return (
    <ol className="flex items-center gap-2">
      {STEPS.map((step, i) => {
        const done = i < current || status === "completed";
        const active = i === current && status !== "completed";
        return (
          <li key={step.id} className="flex flex-1 items-center gap-2">
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold ${
                done
                  ? "border-green-500/40 bg-green-500/20 text-green-300"
                  : active
                    ? "border-accent/50 bg-accent/20 text-accent"
                    : "border-white/15 text-text-muted"
              }`}
            >
              {done ? <CheckIcon size={12} /> : i + 1}
            </span>
            <span
              className={`text-[11px] font-bold uppercase tracking-tight ${
                active
                  ? "text-text-primary"
                  : done
                    ? "text-green-300"
                    : "text-text-muted"
              }`}
            >
              {step.label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="mx-1 h-px flex-1 bg-white/10" aria-hidden />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function StaffOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [phase, setPhase] = useState<Phase>("loading");
  const [fatal, setFatal] = useState<string | null>(null);
  const [user, setUser] = useState<StaffSetupUser | null>(null);

  // Live setup-session token + the resumable pre-token. Refs so async
  // handlers always read the latest value without re-running effects.
  const setupTokenRef = useRef<string | null>(null);
  const preTokenRef = useRef<string | null>(null);

  // Bootstrap runs once. StrictMode double-invoke is guarded so we don't
  // consume the single-use provisioning token twice.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      const urlToken = searchParams.get("token");
      const storedPre = sessionStorage.getItem(SS_PRETOKEN);

      // 1) Try to resume an existing staff_onboarding pre-token (URL first,
      //    then the sessionStorage fallback).
      for (const candidate of [urlToken, storedPre]) {
        if (!candidate) continue;
        try {
          const view = await resumePreToken(candidate);
          if (view && view.flow === "staff_onboarding") {
            const setupToken = String(
              (view.data as { setupSessionToken?: unknown }).setupSessionToken ??
                "",
            );
            if (setupToken) {
              const state = await staffSetupState(setupToken).catch(() => null);
              if (state) {
                adopt(
                  candidate,
                  setupToken,
                  state.user,
                  state.staffAccount.status,
                );
                return;
              }
            }
          }
        } catch (err) {
          // Binding mismatch (different IP/device) is fatal — don't fall
          // through to consuming a provisioning token.
          if (err instanceof ApiError && err.code?.startsWith("pre_token_")) {
            setFatal(err.message);
            setPhase("error");
            return;
          }
        }
      }

      // 2) No resumable pre-token. Treat the URL token as a fresh, single-use
      //    provisioning token.
      if (!urlToken) {
        setFatal(
          "This onboarding link is missing its token. Start again from the main app, under Settings → Account.",
        );
        setPhase("error");
        return;
      }
      try {
        const init = await staffSetupInit(urlToken);
        const started = await startPreToken("staff_onboarding", {
          step: init.staffAccount.status,
          data: { setupSessionToken: init.setupSessionToken },
        });
        adopt(
          started.token,
          init.setupSessionToken,
          init.user,
          init.staffAccount.status,
        );
      } catch (err) {
        setFatal(
          errMessage(
            err,
            "This onboarding link has expired. Start again from the main app, under Settings → Account.",
          ),
        );
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function adopt(
    preTok: string,
    setupTok: string,
    u: StaffSetupUser | null,
    status: StaffSetupStatus,
  ): void {
    preTokenRef.current = preTok;
    setupTokenRef.current = setupTok;
    sessionStorage.setItem(SS_PRETOKEN, preTok);
    sessionStorage.setItem(SS_KEY, setupTok);
    // Keep the URL clean + resumable (pre-token only, never the setup token).
    window.history.replaceState(
      {},
      "",
      `/staff/onboarding?token=${encodeURIComponent(preTok)}`,
    );
    setUser(u);
    setPhase(statusToPhase(status));
  }

  async function advanceTo(status: StaffSetupStatus): Promise<void> {
    const pre = preTokenRef.current;
    if (pre) {
      // Mirror the step into the pre-token so a resume lands correctly even
      // before the next /state read. Best-effort.
      await savePreTokenStep(pre, "staff_onboarding", { step: status }).catch(
        () => undefined,
      );
    }
    setPhase(statusToPhase(status));
  }

  function finish(): void {
    const pre = preTokenRef.current;
    if (pre) void revokePreToken(pre).catch(() => undefined);
    sessionStorage.removeItem(SS_PRETOKEN);
    sessionStorage.removeItem(SS_KEY);
    // The account is provisioned but the user has no staff session yet —
    // send them to sign in with the recovery phrase they just created.
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col px-4 py-10 sm:py-16">
        {/* Header */}
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-accent/30 bg-accent/10 text-accent">
            <ShieldIcon size={18} />
          </span>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-accent">
              Staff onboarding
            </p>
            <h1 className="text-lg font-bold text-text-primary">
              Set up your staff account
            </h1>
          </div>
        </div>

        {phase !== "loading" && phase !== "error" && (
          <div className="mb-8">
            <Stepper
              status={
                phase === "profile"
                  ? "pending_profile"
                  : phase === "security"
                    ? "pending_security"
                    : "completed"
              }
            />
          </div>
        )}

        <div className="flex-1">
          {phase === "loading" && (
            <p className="text-sm text-text-secondary">
              Restoring your onboarding session…
            </p>
          )}

          {phase === "error" && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/[0.06] p-6">
              <h2 className="text-base font-bold text-text-primary">
                Can&rsquo;t continue onboarding
              </h2>
              <p className="mt-2 text-sm text-text-secondary">{fatal}</p>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="btn-primary mt-5"
              >
                Go to staff login
              </button>
            </div>
          )}

          {phase === "profile" && setupTokenRef.current && (
            <ProfileStep
              setupToken={setupTokenRef.current}
              user={user}
              onDone={() => void advanceTo("pending_security")}
            />
          )}

          {phase === "security" && setupTokenRef.current && (
            <SecurityStep
              setupToken={setupTokenRef.current}
              onBack={() => setPhase("profile")}
              onDone={() => void advanceTo("completed")}
            />
          )}

          {phase === "completed" && <CompletedStep onOpen={finish} />}
        </div>

        <p className="mt-10 text-center text-[11px] text-text-muted">
          Your progress is saved automatically — you can close this page and
          reopen the same link to resume where you left off.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Profile
// ---------------------------------------------------------------------------

function ProfileStep({
  setupToken,
  user,
  onDone,
}: {
  setupToken: string;
  user: StaffSetupUser | null;
  onDone: () => void;
}) {
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [bio, setBio] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(skip: boolean): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await staffSetupSaveProfile(
        setupToken,
        skip
          ? {}
          : {
              displayName: displayName.trim() || undefined,
              bio: bio.trim() || undefined,
            },
      );
      onDone();
    } catch (err) {
      setError(errMessage(err, "Couldn't save your staff profile."));
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2 className="text-base font-bold text-text-primary">Staff profile</h2>
      <p className="mt-1 text-sm text-text-secondary">
        Internal identity shown across the back-office. Independent from your
        public account.
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <label className="label">Staff display name</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={user?.displayName ?? "e.g. P.Tweety"}
            maxLength={64}
            className="input"
          />
        </div>
        <div>
          <label className="label">
            Internal note{" "}
            <span className="font-normal normal-case text-text-muted">
              (optional)
            </span>
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Team, focus area, anything teammates should know."
            maxLength={280}
            rows={3}
            className="input resize-none"
          />
        </div>
      </div>

      {error && (
        <p className="mt-4 text-sm text-red-300" role="alert">
          {error}
        </p>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => void save(true)}
          disabled={busy}
          className="text-xs text-text-muted transition-colors hover:text-text-secondary disabled:opacity-40"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={() => void save(false)}
          disabled={busy}
          className="btn-primary"
        >
          {busy ? "Saving…" : "Continue to security"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Security (Ed25519 staff identity + recovery-phrase confirmation)
// ---------------------------------------------------------------------------

type SecuritySub = "intro" | "phrase" | "confirm";

function SecurityStep({
  setupToken,
  onBack,
  onDone,
}: {
  setupToken: string;
  onBack: () => void;
  onDone: () => void;
}) {
  const [sub, setSub] = useState<SecuritySub>("intro");
  const [phrase, setPhrase] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [confirmIdx, setConfirmIdx] = useState<number[]>([]);
  const [confirmInputs, setConfirmInputs] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const words = useMemo(() => phrase?.split(" ") ?? [], [phrase]);

  function generate(): void {
    const m = mnemonic.generate(24);
    setPhrase(m);
    setAck(false);
    setConfirmIdx(pickIndices(24, 3));
    setConfirmInputs({});
    setError(null);
    setSub("phrase");
  }

  async function submit(): Promise<void> {
    if (!phrase) return;
    setError(null);
    // Validate the typed confirmation words client-side first.
    for (const i of confirmIdx) {
      const got = (confirmInputs[i] ?? "").trim().toLowerCase();
      if (got !== words[i]) {
        setError(`Word #${i + 1} doesn't match your recovery phrase.`);
        return;
      }
    }
    setBusy(true);
    try {
      const seed = mnemonic.toSeed(phrase, "");
      const { secretKey, publicKey } = deriveKeyPair(seed);
      const publicKeyHex = hex.fromBytes(publicKey);

      const challenge = await staffSetupSecurityChallenge(
        setupToken,
        publicKeyHex,
      );
      const timestamp = Date.now();
      const message = `${STAFF_REGISTER_MESSAGE_PREFIX}:${publicKeyHex}:${challenge.nonce}:${timestamp}`;
      const signature = hex.fromBytes(sign(secretKey, message));

      await staffSetupSecurityVerify(setupToken, {
        publicKey: publicKeyHex,
        nonce: challenge.nonce,
        timestamp,
        signature,
        wordChecks: confirmIdx.map((i) => ({ index: i, word: words[i]! })),
      });
      onDone();
    } catch (err) {
      setError(errMessage(err, "Couldn't complete the security step."));
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2 className="text-base font-bold text-text-primary">Security</h2>
      <p className="mt-1 text-sm text-text-secondary">
        Your staff identity is a dedicated cryptographic key, separate from your
        public account. Generate and back up its recovery phrase to activate
        back-office access.
      </p>

      {sub === "intro" && (
        <div className="mt-5 space-y-4">
          <ul className="space-y-2 text-sm text-text-secondary">
            <li className="flex gap-2">
              <CheckIcon size={14} className="mt-0.5 shrink-0 text-green-400" />
              A 24-word staff recovery phrase (your back-office login key).
            </li>
            <li className="flex gap-2">
              <CheckIcon size={14} className="mt-0.5 shrink-0 text-green-400" />
              Confirmation that you saved it (3 random words).
            </li>
            <li className="flex gap-2">
              <CheckIcon size={14} className="mt-0.5 shrink-0 text-green-400" />
              Cryptographic proof of control (signed challenge).
            </li>
          </ul>
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={onBack}
              className="text-xs text-text-muted transition-colors hover:text-text-secondary"
            >
              ← Back to profile
            </button>
            <button type="button" onClick={generate} className="btn-primary">
              Generate staff recovery phrase
            </button>
          </div>
        </div>
      )}

      {sub === "phrase" && phrase && (
        <div className="mt-5 space-y-4">
          <ol className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {words.map((w, i) => (
              <li
                key={i}
                className="flex items-center gap-2 rounded-md border border-white/10 bg-bg-elevated px-2.5 py-1.5 text-sm"
              >
                <span className="w-5 shrink-0 text-right text-xs text-text-muted">
                  {i + 1}.
                </span>
                <span className="font-mono text-text-primary">{w}</span>
              </li>
            ))}
          </ol>
          <label className="flex items-start gap-3 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-bg-elevated text-accent"
            />
            <span>
              I have safely written down all 24 words. Losing them means losing
              staff access.
            </span>
          </label>
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => setSub("intro")}
              className="text-xs text-text-muted transition-colors hover:text-text-secondary"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setSub("confirm")}
              disabled={!ack}
              className="btn-primary"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {sub === "confirm" && phrase && (
        <div className="mt-5 space-y-4">
          <p className="text-sm text-text-secondary">
            Type the words at the requested positions to confirm your backup.
          </p>
          <div className="space-y-3">
            {confirmIdx.map((i) => (
              <div key={i}>
                <label className="label">Word #{i + 1}</label>
                <input
                  value={confirmInputs[i] ?? ""}
                  onChange={(e) =>
                    setConfirmInputs((prev) => ({ ...prev, [i]: e.target.value }))
                  }
                  autoComplete="off"
                  spellCheck={false}
                  className="input font-mono"
                />
              </div>
            ))}
          </div>
          {error && (
            <p className="text-sm text-red-300" role="alert">
              {error}
            </p>
          )}
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => setSub("phrase")}
              disabled={busy}
              className="text-xs text-text-muted transition-colors hover:text-text-secondary disabled:opacity-40"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy}
              className="btn-primary"
            >
              {busy ? "Activating…" : "Activate staff account"}
            </button>
          </div>
        </div>
      )}

      {sub === "intro" && error && (
        <p className="mt-4 text-sm text-red-300" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Completed
// ---------------------------------------------------------------------------

function CompletedStep({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="rounded-lg border border-green-500/25 bg-green-500/[0.06] p-6 text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-green-500/40 bg-green-500/20 text-green-300">
        <CheckIcon size={22} />
      </span>
      <h2 className="mt-4 text-base font-bold text-text-primary">
        Staff account active
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
        Your staff identity is provisioned. Sign in with your new recovery
        phrase to access the back-office and internal tools.
      </p>
      <div className="mt-6 flex justify-center">
        <button type="button" onClick={onOpen} className="btn-primary">
          Continue to staff login
        </button>
      </div>
    </div>
  );
}

export default StaffOnboarding;
